/**
 * Perspective persistence helpers for DataTable.
 * Handles cookie/localStorage read-write and perspective settings sanitization.
 * Pure functions — no React hooks, no side effects beyond storage writes.
 */
import type { PerspectiveSettings } from '@open-mercato/shared/modules/perspectives/types'

export const PERSPECTIVE_COOKIE_PREFIX = 'om_table_perspective'
export const PERSPECTIVE_STORAGE_PREFIX = 'om_table_perspective_snapshot'

export type PerspectiveSnapshot = {
  perspectiveId: string | null
  settings: PerspectiveSettings
  updatedAt: number
}

export function readPerspectiveCookie(tableId: string): string | null {
  if (typeof document === 'undefined') return null
  const key = `${PERSPECTIVE_COOKIE_PREFIX}:${tableId}`
  const pattern = new RegExp(`(?:^|;\\s*)${key}=([^;]+)`)
  const match = document.cookie.match(pattern)
  return match ? decodeURIComponent(match[1]) : null
}

export function writePerspectiveCookie(tableId: string, perspectiveId: string | null): void {
  if (typeof document === 'undefined') return
  const key = `${PERSPECTIVE_COOKIE_PREFIX}:${tableId}`
  const expires = perspectiveId ? 'Max-Age=31536000' : 'Max-Age=0'
  const value = perspectiveId ? encodeURIComponent(perspectiveId) : ''
  document.cookie = `${key}=${value}; Path=/; ${expires}; SameSite=Lax`
}

export function readPerspectiveSnapshot(tableId: string): PerspectiveSnapshot | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(`${PERSPECTIVE_STORAGE_PREFIX}:${tableId}`)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const perspectiveId =
      typeof parsed.perspectiveId === 'string' && parsed.perspectiveId.trim().length > 0
        ? parsed.perspectiveId
        : null
    const settings = typeof parsed.settings === 'object' && parsed.settings !== null
      ? parsed.settings as PerspectiveSettings
      : null
    const updatedAt = typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now()
    if (!settings) return null
    return { perspectiveId, settings, updatedAt }
  } catch {
    return null
  }
}

export function writePerspectiveSnapshot(tableId: string, snapshot: PerspectiveSnapshot | null) {
  if (typeof window === 'undefined') return
  const key = `${PERSPECTIVE_STORAGE_PREFIX}:${tableId}`
  try {
    if (!snapshot) {
      window.localStorage.removeItem(key)
      return
    }
    window.localStorage.setItem(key, JSON.stringify(snapshot))
  } catch {
    // ignore storage errors
  }
}

export function sanitizePerspectiveSettings(source?: PerspectiveSettings | null): PerspectiveSettings | null {
  if (!source || typeof source !== 'object') return null
  const forbidden = new Set(['__proto__', 'prototype', 'constructor'])
  const result: PerspectiveSettings = {}

  if (Array.isArray(source.columnOrder)) {
    const seen = new Set<string>()
    const order = source.columnOrder
      .map((id) => (typeof id === 'string' ? id.trim() : ''))
      .filter((id) => id.length > 0 && !seen.has(id) && (seen.add(id), true))
    if (order.length) result.columnOrder = order
  }

  if (source.columnVisibility && typeof source.columnVisibility === 'object') {
    const entries = Object.entries(source.columnVisibility)
      .filter(([key, value]) => typeof key === 'string' && key.trim().length > 0 && !forbidden.has(key) && typeof value === 'boolean')
    if (entries.length) {
      const visibility: Record<string, boolean> = {}
      entries.forEach(([key, value]) => { visibility[key] = value })
      result.columnVisibility = visibility
    }
  }

  if (Array.isArray(source.sorting)) {
    const sorting = source.sorting
      .map((item) => {
        const id = typeof item?.id === 'string' ? item.id.trim() : ''
        if (!id || forbidden.has(id)) return null
        return { id, desc: Boolean(item?.desc) }
      })
      .filter((item): item is { id: string; desc: boolean } => item !== null)
    if (sorting.length) result.sorting = sorting
  }

  if (typeof source.pageSize === 'number' && Number.isFinite(source.pageSize)) {
    const pageSize = Math.max(1, Math.min(500, Math.floor(source.pageSize)))
    result.pageSize = pageSize
  }

  if (typeof source.searchValue === 'string' && source.searchValue.trim().length > 0) {
    result.searchValue = source.searchValue.trim().slice(0, 200)
  }

  if (source.filters && typeof source.filters === 'object') {
    const filters: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(source.filters)) {
      if (typeof key === 'string') {
        const trimmed = key.trim()
        if (trimmed.length > 0 && !forbidden.has(trimmed)) filters[trimmed] = value
      }
    }
    if (Object.keys(filters).length) result.filters = filters
  }

  return Object.keys(result).length ? result : null
}

export function formatDurationLabel(durationMs?: number | null): string {
  if (durationMs == null) return ''
  if (!Number.isFinite(durationMs)) return ''
  if (durationMs < 0) return ''
  if (durationMs < 1000) return `${Math.round(durationMs)}ms`
  if (durationMs < 10_000) return `${(durationMs / 1000).toFixed(1)}s`
  if (durationMs < 60_000) return `${Math.round(durationMs / 1000)}s`
  if (durationMs < 3_600_000) return `${(durationMs / 60_000).toFixed(durationMs < 600_000 ? 1 : 0)}m`
  return `${(durationMs / 3_600_000).toFixed(durationMs < 7_200_000 ? 1 : 0)}h`
}
